import { unwrap } from "@sagot/api-client";
import { AUTO_TOPUP_MAX_PACKS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Input, Switch, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { useApi } from "../../../session/session-provider";
import { useFormatters, type BillingOverview } from "../shared";
import { GatedButton } from "./gated-button";
import { useInvalidateBilling } from "./use-billing";

type AutoTopUpSettings = BillingOverview["auto_topup"];

interface AutoTopUpBody {
  enabled: boolean;
  packs: number;
  threshold: number;
  monthly_cap: string | null;
  overage_grace: boolean;
}

function bodyOf(settings: AutoTopUpSettings): AutoTopUpBody {
  return { enabled: settings.enabled, packs: settings.packs, threshold: settings.threshold, monthly_cap: settings.monthly_cap, overage_grace: settings.overage_grace };
}

function useSaveAutoTopUp() {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (body: AutoTopUpBody) => unwrap(api.PUT("/v1/billing/auto-topup", { body })),
    onSuccess: async (saved) => {
      toast.success(saved.enabled ? t("billing.topup.on") : t("billing.topup.off"));
      await invalidate();
    },
    onError: (caught) => toast.error(message(caught)),
  });
}

const schema = z.object({
  packs: z.coerce.number().int().min(1).max(AUTO_TOPUP_MAX_PACKS),
  threshold: z.coerce.number().int().min(0),
  cap: z.string().trim().regex(/^(\d+(\.\d{1,2})?)?$/),
  grace: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

function SettingsDialog({ settings, onClose }: { settings: AutoTopUpSettings; onClose: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const save = useSaveAutoTopUp();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { packs: settings.packs, threshold: settings.threshold, cap: settings.monthly_cap ?? "", grace: settings.overage_grace },
  });
  const submit = form.handleSubmit((values) => {
    save.mutate(
      { enabled: true, packs: values.packs, threshold: values.threshold, monthly_cap: values.cap || null, overage_grace: values.grace },
      { onSuccess: onClose },
    );
  });
  const errors = form.formState.errors;
  const grace = useWatch({ control: form.control, name: "grace" });
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t("billing.topup.settingsTitle")} description={t("billing.topup.pack", { price: fmt.money(settings.pack_price, settings.currency), credits: fmt.number(settings.pack_credits) })} closeLabel={t("common.close")} className="max-w-md"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form="topup-form" loading={save.isPending}>
              {t("billing.topup.saveOn")}
            </Button>
          </>
        }
      >
        <form id="topup-form" className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("billing.topup.packs")} hint={t("billing.topup.packsHint", { max: AUTO_TOPUP_MAX_PACKS })} error={errors.packs ? t("billing.topup.packsError", { max: AUTO_TOPUP_MAX_PACKS }) : undefined}>
              <Input type="number" min={1} max={AUTO_TOPUP_MAX_PACKS} inputMode="numeric" {...form.register("packs")} />
            </Field>
            <Field label={t("billing.topup.threshold")} hint={t("billing.topup.thresholdHint")} error={errors.threshold ? t("billing.topup.numberError") : undefined}>
              <Input type="number" min={0} inputMode="numeric" {...form.register("threshold")} />
            </Field>
          </div>
          <Field label={t("billing.topup.cap", { currency: settings.currency })} hint={t("billing.topup.capHint")} error={errors.cap ? t("billing.topup.numberError") : undefined}>
            <Input inputMode="decimal" placeholder="4500" {...form.register("cap")} />
          </Field>
          <div className="flex items-center justify-between gap-3 rounded-md border border-line px-3 py-2.5">
            <span>
              <span id="grace-label" className="block text-sm font-semibold">{t("billing.topup.grace")}</span>
              <span id="grace-hint" className="block text-xs text-fg-muted">{t("billing.topup.graceHint")}</span>
            </span>
            <Switch checked={grace} onCheckedChange={(value) => { form.setValue("grace", value); }} aria-labelledby="grace-label" aria-describedby="grace-hint" />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Auto top-up switch + settings (mockup: bordered pill next to the overage policy). */
export function AutoTopUp({ settings, canManage }: { settings: AutoTopUpSettings; canManage: boolean }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const save = useSaveAutoTopUp();
  const [editing, setEditing] = useState(false);
  return (
    <div className="flex shrink-0 items-center gap-3 rounded-md border border-line px-3 py-2">
      <div>
        <div className="text-sm font-semibold" id="topup-label">
          {t("billing.topup.title")}
        </div>
        <div className="text-[11px] text-fg-subtle">{t("billing.topup.pack", { price: fmt.money(settings.pack_price, settings.currency), credits: fmt.number(settings.pack_credits) })}</div>
      </div>
      <GatedButton allowed={canManage} variant="ghost" size="sm" iconOnly aria-label={t("billing.topup.settingsTitle")} onClick={() => { setEditing(true); }}>
        <Icon name="sliders-horizontal" />
      </GatedButton>
      <Switch
        aria-labelledby="topup-label"
        checked={settings.enabled}
        disabled={!canManage || save.isPending}
        onCheckedChange={(enabled) => { save.mutate({ ...bodyOf(settings), enabled }); }}
      />
      {editing ? <SettingsDialog settings={settings} onClose={() => { setEditing(false); }} /> : null}
    </div>
  );
}
